import { createSelector } from '@ngrx/store';
import { getMultipleOrderOnlineState } from '../reducers';
import { selectActiveProductTypeEntity } from 'app/shared/upload-document/redux/selectors/product-type.selector';
import { selectActiveShipmentLocationEntity } from 'app/shared/upload-document/redux/selectors/shipment-location.selector';
import { selectActiveShippingConditionEntity } from 'app/shared/upload-document/redux/selectors/shipping-condition.selector';
import {
  PP_ABD_DEFAULT_VALUE,
  PP_ABD_MAX_VALUE,
  PP_PUMP_MED_MULTIPLE_VALUE,
  PP_PUMP_MED_VALUE,
  PP_QTY_DEFAULT_VALUE,
  PP_QTY_MAX_VALUE,
  PP_QTY_MIN_VALUE,
} from 'app/shared/pumping/pumping.constant';

export const getOrdersCatalogsState = createSelector(getMultipleOrderOnlineState, (state) => state.ordersCatalogs);

export const getValuesAddedState = createSelector(getMultipleOrderOnlineState, (state) => state.valuesAdded);

export const getApplications = createSelector(getOrdersCatalogsState, (state) => state && state.applications);

export const getUnloadTypes = createSelector(getOrdersCatalogsState, (state) => state && state.unloadTypes);

export const getSpacings = createSelector(getOrdersCatalogsState, (state) => state && state.spacings);

export const getUnloadTimes = createSelector(getOrdersCatalogsState, (state) => state && state.unloadTimes);

export const getPumpCapacities = createSelector(getOrdersCatalogsState, (state) => state && state.pumpCapacities);

export const getFilteredUnloadTimes = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.filteredUnloadTimes,
);

export const getLoadSizes = createSelector(getOrdersCatalogsState, (state) => state && state.loadSizes);

export const selectActiveShipmentLocationEntityAndProductTypeAndShippingCondition = createSelector(
  selectActiveShipmentLocationEntity,
  selectActiveProductTypeEntity,
  selectActiveShippingConditionEntity,
  (activeShipmentLocation, activeProductType, activeShippingCondition) => ({
    activeProductType,
    activeShipmentLocation,
    activeShippingCondition,
  }),
);

export const getProducts = createSelector(getOrdersCatalogsState, (state) => state && state.availableProducts);

export const getValuesAddedNotCombinable = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.valuesAddedNotCombinable,
);

export const getSlumps = (productCode: string) =>
  createSelector(getOrdersCatalogsState, (state) => {
    if (state && state.slumpProduct && productCode) {
      productCode = productCode.replace(/\b0+/g, '');
      return state.slumpProduct[productCode];
    }
    return undefined;
  });

export const getAllSlumps = createSelector(getOrdersCatalogsState, (state) => state && state.slumpProduct);

export const getContacts = createSelector(getOrdersCatalogsState, (state) => state && state.contacts);

export const getAvailableValuesAdded = createSelector(
  getValuesAddedState,
  (state) => state && state.availableValuesAdded,
);

export const getValuesAddedForKey = (contractCodeKeyName: string) => {
  return createSelector(getAvailableValuesAdded, (availables) => {
    return availables[contractCodeKeyName];
  });
};

export const getPODs = createSelector(getOrdersCatalogsState, (state) => state && state.pointOfDeliveries);

export const getFeatureIceQuantity = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.featureIceQuantity,
);

export const getFeatureTemperatureManagement = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.featureTemperature,
);

/**
 * Selector to get default quantity for Pump Primer feature
 */
export const getPumpPrimerDefaultQuantity = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.featurePumpPrimer.find((feature) => feature.featureCode === PP_QTY_DEFAULT_VALUE),
);

/**
 * Selector to get max of arrival delivery feature
 */
export const getMaxArrivalDelivery = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.featurePumpPrimer.find((feature) => feature.featureCode === PP_ABD_MAX_VALUE),
);

/**
 * Selector to get Pump method feature
 * @returns a dictionary of Pump method features
 */
export const getPumpMethodValue = createSelector(
  getOrdersCatalogsState,
  (state) =>
    state &&
    state.featurePumpPrimer
      .filter(
        (feature) => feature.featureCode === PP_PUMP_MED_VALUE || feature.featureCode === PP_PUMP_MED_MULTIPLE_VALUE,
      )
      .reduce((acc, feature) => {
        acc[feature.featureCode] = feature;
        return acc;
      }, {}),
);

/**
 * Selector to get default arrival delivery feature
 */
export const getArrivalDeliveryDefault = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.featurePumpPrimer.find((feature) => feature.featureCode === PP_ABD_DEFAULT_VALUE),
);

/**
 * Selector to get max for Pump Primer Mix quantity feature
 */
export const getMaxQuantityPrimer = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.featurePumpPrimer.find((feature) => feature.featureCode === PP_QTY_MAX_VALUE),
);

/**
 * Selector to get min for Pump Primer Mix quantity feature
 */
export const getMinQuantityPrimer = createSelector(
  getOrdersCatalogsState,
  (state) => state && state.featurePumpPrimer.find((feature) => feature.featureCode === PP_QTY_MIN_VALUE),
);
